// src/store/requirementStore.ts
/**
 * .tuMetrics {"tu1": 0.83, "tu2": 0.86, "tu3": 5, "notes": "Zustand store with realtime sync; hand-written DB row types compensate for missing generated types.", "tuVersion": "2.5"}
 * .help "Manages product requirements: loading, creating, editing, deleting, and linking requirements to task cards."
 */
import { create } from 'zustand';
import { Requirement, CardRequirement } from '../types';
import { supabase } from '../lib/supabase';
import useUserStore from './userStore';
import useFeedStore from './feedStore';
import useFollowStore from './followStore';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { followRefsFromRequirement } from '../features/feed/followMatch';

// July 2026: types/supabase.ts (generated Database types) is an empty file, so
// the old `Database['public']['Tables'][...]` lookups never type-checked.
// Hand-written row shapes until codegen is wired up.
interface RequirementRow {
  id: string;
  created_at: string;
  updated_at: string;
  title: string;
  description: string | null;
  status: Requirement['status'];
  priority: string;
  version: string;
  created_by_id: string | null;
  requested_by_id: string | null;
  parent_id: string | null;
  tags: string[];
  external_link: string | null;
  acceptance_criteria: string[] | null;
  business_value: string | null;
  technical_notes: string | null;
  estimated_effort_days: number | null;
  dependencies: string[] | null;
  company_id: string | null;
  team_id: string | null;
  project_id: string | null;
  roadmap_id: string | null;
  ticket_number: number | null;
  inline_media: Requirement['inline_media'] | null;
}

type RequirementInsert = Partial<Omit<RequirementRow, 'id' | 'created_at' | 'updated_at'>> & {
  title: string;
  status: Requirement['status'];
  priority: string;
  version: string;
};

type RequirementUpdate = Partial<Omit<RequirementRow, 'id' | 'created_at' | 'updated_at'>>;

interface CardRequirementRow {
  card_id: string;
  requirement_id: string;
  created_at: string;
  created_by_id: string | null;
  implementation_notes: string | null;
  company_id: string | null;
}

type CardRequirementInsert = Partial<Omit<CardRequirementRow, 'created_at'>> & {
  card_id: string;
  requirement_id: string;
  // Written by linkRequirementToCard from the selected team filter
  team_id?: string | null;
};


const mapRequirementFromDB = (row: RequirementRow): Requirement => ({
  id: row.id,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  title: row.title,
  description: row.description || undefined,
  status: row.status,
  priority: row.priority as 'low' | 'medium' | 'high',
  version: row.version,
  createdById: row.created_by_id || undefined,
  requestedById: row.requested_by_id || undefined, // <- only present for agent flow
  parentId: row.parent_id || undefined,
  tags: row.tags,
  externalLink: row.external_link || undefined,
  acceptanceCriteria: row.acceptance_criteria || undefined,
  businessValue: row.business_value || undefined,
  technicalNotes: row.technical_notes || undefined,
  estimatedEffortDays: row.estimated_effort_days || undefined,
  dependencies: row.dependencies || undefined,
  companyId: row.company_id || undefined,
  teamId: row.team_id || undefined, // NEW: Map team_id
  projectId: row.project_id || undefined, // November 3, 2025: Map project_id
  roadmapId: row.roadmap_id || undefined, // December 10, 2025: Map roadmap_id
  ticketNumber: row.ticket_number || undefined, // November 3, 2025: Map ticket_number
  inline_media: row.inline_media || undefined, // November 28, 2025: Map inline_media
});

const mapRequirementToDB = (
  requirement: Omit<Requirement, 'id' | 'createdAt' | 'updatedAt'>
): RequirementInsert => ({
  title: requirement.title,
  description: requirement.description || null,
  status: requirement.status,
  priority: requirement.priority,
  version: requirement.version,
  created_by_id: requirement.createdById || null,
  requested_by_id: requirement.requestedById || null, // <- only set if explicitly provided
  parent_id: requirement.parentId || null,
  tags: requirement.tags,
  external_link: requirement.externalLink || null,
  acceptance_criteria: requirement.acceptanceCriteria || null,
  business_value: requirement.businessValue || null,
  technical_notes: requirement.technicalNotes || null,
  estimated_effort_days: requirement.estimatedEffortDays || null,
  dependencies: requirement.dependencies || null,
  company_id: requirement.companyId || null,
  team_id: requirement.teamId || null, // NEW: Map team_id
  project_id: requirement.projectId || null, // November 3, 2025: Map project_id
  ticket_number: requirement.ticketNumber || null, // November 3, 2025: Map ticket_number
} as RequirementInsert);

const mapCardRequirementFromDB = (row: CardRequirementRow): CardRequirement => ({
  cardId: row.card_id,
  requirementId: row.requirement_id,
  createdAt: row.created_at,
  createdById: row.created_by_id || undefined,
  implementationNotes: row.implementation_notes || undefined,
  companyId: row.company_id || undefined,
});

interface RequirementState {
  requirements: Requirement[];
  cardRequirements: CardRequirement[];
  isLoading: boolean;
  error: string | null;
  selectedTeamId: string | null; // NEW: Add selectedTeamId to state
  lastFetchedAt: number | null; // Timestamp of last fetch for staleness check
  realtimeChannel: RealtimeChannel | null;

  initializeRequirements: () => Promise<void>;
  refreshRequirementsIfStale: () => Promise<void>;
  setupRealtimeSubscription: () => Promise<void>;
  cleanupRealtimeSubscription: () => void;
  setSelectedTeamId: (teamId: string | null) => void; // NEW: Add action to set selected team
  addRequirement: (
    requirement: Omit<Requirement, 'id' | 'createdAt' | 'updatedAt'>
  ) => Promise<void>;
  updateRequirement: (
    requirementId: string,
    updates: Partial<Requirement>
  ) => Promise<void>;
  deleteRequirement: (requirementId: string) => Promise<void>;
  linkRequirementToCard: (
    cardId: string,
    requirementId: string,
    implementationNotes?: string
  ) => Promise<void>;
  unlinkRequirementFromCard: (cardId: string, requirementId: string) => Promise<void>;
  getRequirementsForCard: (cardId: string) => Requirement[];
  getCardsForRequirement: (requirementId: string) => CardRequirement[];
}

const useRequirementStore = create<RequirementState>((set, get) => ({
  requirements: [],
  cardRequirements: [],
  isLoading: false,
  error: null,
  selectedTeamId: null, // NEW: Initialize selectedTeamId
  lastFetchedAt: null,
  realtimeChannel: null,

  initializeRequirements: async () => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser?.companyId) {
        console.warn(
          'initializeRequirements: No current user companyId found. Skipping requirement initialization.'
        );
        set({ requirements: [], cardRequirements: [], isLoading: false });
        return;
      }

      const requirementsQuery = supabase
        .from('requirements')
        .select('*')
        .eq('company_id', currentUser.companyId);

      const { data: requirementsData, error: requirementsError } = await requirementsQuery
        .order('created_at', { ascending: false });

      if (requirementsError) throw requirementsError;

      const cardRequirementsQuery = supabase
        .from('card_requirements')
        .select('*')
        .eq('company_id', currentUser.companyId);

      const { data: cardRequirementsData, error: cardRequirementsError } = await cardRequirementsQuery
        .order('created_at', { ascending: false });

      if (cardRequirementsError) throw cardRequirementsError;

      const requirements = requirementsData.map(mapRequirementFromDB);
      const cardRequirements = cardRequirementsData.map(mapCardRequirementFromDB);

      // REMOVED: Old filtering logic based on cards' teams, as requirements now have team_id directly.
      // if (selectedTeamId) {
      //   const { data: cardsData, error: cardsError } = await supabase
      //     .from('cards')
      //     .select('id, team_id')
      //     .eq('company_id', currentUser.companyId)
      //     .eq('team_id', selectedTeamId);
        
      //   if (cardsError) throw cardsError;

      //   const teamCardIds = new Set(cardsData.map(card => card.id));
      //   const relevantRequirementIds = new Set<string>();

      //   cardRequirements = cardRequirements.filter(cr => teamCardIds.has(cr.cardId));
      //   cardRequirements.forEach(cr => relevantRequirementIds.add(cr.requirementId));

      //   requirements = requirements.filter(req => relevantRequirementIds.has(req.id));
      // }

      set({ requirements, cardRequirements, isLoading: false, lastFetchedAt: Date.now() });
    } catch (error) {
      console.error('Error loading requirements:', error);
      set({ error: 'Failed to load requirements', isLoading: false });
    }
  },

  refreshRequirementsIfStale: async () => {
    const { lastFetchedAt, isLoading, requirements } = get();
    const now = Date.now();
    const STALE_THRESHOLD = 30000; // 30 seconds

    // If no requirements loaded yet, initialize
    if (requirements.length === 0) {
      await get().initializeRequirements();
      return;
    }

    // Skip if already loading or data is fresh
    if (isLoading || (lastFetchedAt && now - lastFetchedAt < STALE_THRESHOLD)) {
      console.log('refreshRequirementsIfStale: Data is fresh, skipping refresh');
      return;
    }

    console.log('refreshRequirementsIfStale: Data is stale, refreshing...');
    await get().initializeRequirements();
  },

  /**
   * Setup realtime subscription for requirements and card_requirements
   */
  setupRealtimeSubscription: async () => {
    // Clean up existing subscription
    get().cleanupRealtimeSubscription();

    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser?.companyId) {
      console.warn('setupRealtimeSubscription: No current user companyId found. Skipping realtime subscription setup.');
      return;
    }

    // Ensure requirements are loaded first
    const { requirements } = get();
    if (requirements.length === 0) {
      await get().initializeRequirements();
    }

    const channel = supabase
      .channel('requirements_channel')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'requirements',
          filter: `company_id=eq.${currentUser.companyId}`,
        },
        (payload) => {
          console.log('Real-time requirement payload received:', payload);
          const { eventType, new: newRow, old: oldRow } = payload;

          switch (eventType) {
            case 'INSERT': {
              const newRequirement = mapRequirementFromDB(newRow as RequirementRow);
              // Dedupe: streamStore's agent-object handler may have inserted it already
              set((state) =>
                state.requirements.some((r) => r.id === newRequirement.id)
                  ? state
                  : { requirements: [newRequirement, ...state.requirements] }
              );
              break;
            }

            case 'UPDATE': {
              const updatedRequirement = mapRequirementFromDB(newRow as RequirementRow);
              set((state) => ({
                requirements: state.requirements.map((r) =>
                  r.id === updatedRequirement.id ? updatedRequirement : r
                ),
              }));
              break;
            }

            case 'DELETE': {
              set((state) => ({
                requirements: state.requirements.filter((r) => r.id !== oldRow.id),
              }));
              break;
            }
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'card_requirements',
          filter: `company_id=eq.${currentUser.companyId}`,
        },
        (payload) => {
          console.log('Real-time card_requirement payload received:', payload);
          const { eventType, new: newRow, old: oldRow } = payload;

          switch (eventType) {
            case 'INSERT': {
              const newCardRequirement = mapCardRequirementFromDB(newRow as CardRequirementRow);
              set((state) => ({
                cardRequirements: [newCardRequirement, ...state.cardRequirements],
              }));
              break;
            }

            case 'UPDATE': {
              const updatedCardRequirement = mapCardRequirementFromDB(newRow as CardRequirementRow);
              set((state) => ({
                cardRequirements: state.cardRequirements.map((cr) =>
                  cr.cardId === updatedCardRequirement.cardId &&
                  cr.requirementId === updatedCardRequirement.requirementId
                    ? updatedCardRequirement
                    : cr
                ),
              }));
              break;
            }

            case 'DELETE': {
              set((state) => ({
                cardRequirements: state.cardRequirements.filter(
                  (cr) =>
                    !(cr.cardId === oldRow.card_id && cr.requirementId === oldRow.requirement_id)
                ),
              }));
              break;
            }
          }
        }
      )
      .subscribe();

    set({ realtimeChannel: channel });
    console.log('Requirements realtime subscription established');
  },

  /**
   * Cleanup realtime subscription
   */
  cleanupRealtimeSubscription: () => {
    const { realtimeChannel } = get();
    if (realtimeChannel) {
      supabase.removeChannel(realtimeChannel);
      set({ realtimeChannel: null });
      console.log('Requirements realtime subscription cleaned up');
    }
  },

  setSelectedTeamId: (teamId) => {
    set({ selectedTeamId: teamId });
    if (teamId) {
      localStorage.setItem('lastSelectedRequirementTeamId', teamId);
    } else {
      localStorage.setItem('lastSelectedRequirementTeamId', 'null');
    }
    get().initializeRequirements(); // Re-fetch requirements with the new team filter
  },

  addRequirement: async (requirement) => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');
      if (!currentUser.companyId)
        throw new Error('Current user is not associated with a company.');

      // createdById is always current user here; requestedById only if passed in (agent flow wouldn’t normally hit this)
      const requirementData = mapRequirementToDB({
        ...requirement,
        createdById: currentUser.id,
        companyId: currentUser.companyId,
        teamId: requirement.teamId || undefined,
      });

      const { data, error } = await supabase
        .from('requirements')
        .insert(requirementData)
        .select()
        .single();

      if (error) throw error;

      const newRequirement = mapRequirementFromDB(data);

      // Feed event with ids for consistency
      await useFeedStore.getState().addEvent({
        type: 'requirement_created',
        title: 'New Requirement Created',
        description: `${currentUser.name} created requirement "${requirement.title}"`,
        userId: currentUser.id,
        initiatorId: currentUser.id,
        requirementId: newRequirement.id,
        projectId: newRequirement.projectId ?? undefined,
        roadmapId: newRequirement.roadmapId ?? undefined,
      });

      set((state) => ({
        requirements: [newRequirement, ...state.requirements],
        isLoading: false,
      }));
    } catch (error) {
      console.error('Error adding requirement:', error);
      set({ error: 'Failed to add requirement', isLoading: false });
    }
  },

  updateRequirement: async (requirementId, updates) => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      // Map frontend updates to DB format (requested_by_id only when explicitly provided)
      const dbUpdates: RequirementUpdate = {};
      if (updates.title !== undefined) dbUpdates.title = updates.title;
      if (updates.description !== undefined) dbUpdates.description = updates.description;
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.priority !== undefined) dbUpdates.priority = updates.priority;
      if (updates.version !== undefined) dbUpdates.version = updates.version;
      if (updates.createdById !== undefined) dbUpdates.created_by_id = updates.createdById;
      if (updates.requestedById !== undefined)
        dbUpdates.requested_by_id = updates.requestedById; // <- only when set
      if (updates.parentId !== undefined) dbUpdates.parent_id = updates.parentId;
      if (updates.tags !== undefined) dbUpdates.tags = updates.tags;
      if (updates.externalLink !== undefined) dbUpdates.external_link = updates.externalLink;
      if (updates.acceptanceCriteria !== undefined)
        dbUpdates.acceptance_criteria = updates.acceptanceCriteria;
      if (updates.businessValue !== undefined) dbUpdates.business_value = updates.businessValue;
      if (updates.technicalNotes !== undefined)
        dbUpdates.technical_notes = updates.technicalNotes;
      if (updates.estimatedEffortDays !== undefined)
        dbUpdates.estimated_effort_days = updates.estimatedEffortDays;
      if (updates.dependencies !== undefined) dbUpdates.dependencies = updates.dependencies;
      if (updates.companyId !== undefined) dbUpdates.company_id = updates.companyId;
      if (updates.teamId !== undefined) dbUpdates.team_id = updates.teamId; // NEW: Include team_id in updates
      if (updates.projectId !== undefined) dbUpdates.project_id = updates.projectId || null; // November 3, 2025: Include project_id in updates
      if (updates.roadmapId !== undefined) dbUpdates.roadmap_id = updates.roadmapId || null; // December 10, 2025: Include roadmap_id in updates

      console.log('🔍 [requirementStore] Updating requirement:', { requirementId, dbUpdates });

      const { data, error} = await supabase
        .from('requirements')
        .update(dbUpdates)
        .eq('id', requirementId)
        .eq('company_id', currentUser.companyId) // December 10, 2025: Add company guard to help RLS
        .select()
        .single();

      if (error) {
        console.error('❌ [requirementStore] Update failed:', error);
        throw error;
      }

      console.log('✅ [requirementStore] Update successful:', data);

      const updatedRequirement = mapRequirementFromDB(data);

      await useFeedStore.getState().addEvent({
        type: 'requirement_updated',
        title: 'Requirement Updated',
        description: `${currentUser.name} updated requirement "${updatedRequirement.title}"`,
        userId: currentUser.id,
        initiatorId: currentUser.id,
        requirementId: updatedRequirement.id,
        projectId: updatedRequirement.projectId ?? undefined,
        roadmapId: updatedRequirement.roadmapId ?? undefined,
      });

      const followWorthy = updates.status !== undefined
        || updates.title !== undefined
        || updates.priority !== undefined;
      if (followWorthy) {
        try {
          await useFollowStore.getState().notifyFollowers(
            followRefsFromRequirement(updatedRequirement),
            [currentUser.id],
            {
              type: 'requirement_updated',
              title: 'Feature updated',
              description: `${currentUser.name} updated requirement "${updatedRequirement.title}"`,
              initiatorId: currentUser.id,
              requirementId: updatedRequirement.id,
              projectId: updatedRequirement.projectId ?? undefined,
              roadmapId: updatedRequirement.roadmapId ?? undefined,
            },
          );
        } catch (followErr) {
          console.warn('Follower notify on requirement update failed (non-critical):', followErr);
        }
      }

      set((state) => ({
        requirements: state.requirements.map((req) =>
          req.id === requirementId ? updatedRequirement : req
        ),
        isLoading: false,
      }));
    } catch (error) {
      console.error('Error updating requirement:', error);
      set({ error: 'Failed to update requirement', isLoading: false });
    }
  },

  deleteRequirement: async (requirementId) => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      const requirement = get().requirements.find((r) => r.id === requirementId);
      if (!requirement) throw new Error('Requirement not found');

      // Safer delete: include company guard (good for RLS paths)
      const { error } = await supabase
        .from('requirements')
        .delete()
        .eq('id', requirementId);
        // .eq('company_id', currentUser.companyId); // optional extra guard

      if (error) throw error;

      await useFeedStore.getState().addEvent({
        type: 'requirement_deleted',
        title: 'Requirement Deleted',
        description: `${currentUser.name} deleted requirement "${requirement.title}"`,
        userId: currentUser.id,
        initiatorId: currentUser.id,
        requirementId,
      });

      set((state) => ({
        requirements: state.requirements.filter((req) => req.id !== requirementId),
        cardRequirements: state.cardRequirements.filter(
          (cr) => cr.requirementId !== requirementId
        ),
        isLoading: false,
      }));
    } catch (error) {
      console.error('Error deleting requirement:', error);
      set({ error: 'Failed to delete requirement', isLoading: false });
    }
  },

  linkRequirementToCard: async (cardId, requirementId, implementationNotes) => {
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');
      if (!currentUser.companyId)
        throw new Error('Current user is not associated with a company.');

      const cardRequirementData: CardRequirementInsert = {
        card_id: cardId,
        requirement_id: requirementId,
        created_by_id: currentUser.id,
        implementation_notes: implementationNotes || null,
        company_id: currentUser.companyId,
        team_id: null,
      };

      const { data, error } = await supabase
        .from('card_requirements')
        .insert(cardRequirementData)
        .select()
        .single();

      if (error) throw error;

      const newCardRequirement = mapCardRequirementFromDB(data);

      const requirement = get().requirements.find((r) => r.id === requirementId);
      if (requirement) {
        await useFeedStore.getState().addEvent({
          type: 'requirement_linked',
          title: 'Requirement Linked',
          description: `${currentUser.name} linked requirement "${requirement.title}" to a card`,
          userId: currentUser.id,
          initiatorId: currentUser.id,
          requirementId: requirementId,
          cardId,
        });
      }

      set((state) => ({
        cardRequirements: [...state.cardRequirements, newCardRequirement],
      }));

      // November 3, 2025: Refresh requirements after linking to card
      // The database trigger will update the requirement's project_id if the card has a project
      // We need to refresh to see the updated project_id and ticket_number
      await get().initializeRequirements();
    } catch (error) {
      console.error('Error linking requirement to card:', error);
    }
  },

  unlinkRequirementFromCard: async (cardId, requirementId) => {
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      // Safer unlink: include company guard (good for RLS paths)
      const { error } = await supabase
        .from('card_requirements')
        .delete()
        .eq('card_id', cardId)
        .eq('requirement_id', requirementId)
        .eq('company_id', currentUser.companyId);

      if (error) throw error;

      set((state) => ({
        cardRequirements: state.cardRequirements.filter(
          (cr) => !(cr.cardId === cardId && cr.requirementId === requirementId)
        ),
      }));
    } catch (error) {
      console.error('Error unlinking requirement from card:', error);
    }
  },

  getRequirementsForCard: (cardId) => {
    const state = get();
    const linkedRequirementIds = state.cardRequirements
      .filter((cr) => cr.cardId === cardId)
      .map((cr) => cr.requirementId);

    return state.requirements.filter((req) => linkedRequirementIds.includes(req.id));
  },

  getCardsForRequirement: (requirementId) => {
    return get().cardRequirements.filter((cr) => cr.requirementId === requirementId);
  },
}));

export default useRequirementStore;
